// import React from 'react';
import { useState, useEffect } from 'react'; // React
import { useNavigate } from 'react-router-dom';
import { UserTable } from '@/components/features/users/UserTable';
// import { Button } from '@/components/ui/button';
import { userService } from '@/services/api/services/userService';
import { User } from '@/types/user.types';
import { useTranslation } from 'react-i18next';
import { Users2, UserPlus, RefreshCw } from 'lucide-react';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';

export const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleApiError } = useApiErrorHandler();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Security validation function
  const validateSecurityAccess = () => {
    if (!user?.id) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      navigate('/');
      return;
    }

    // Only Administrators can access user management
    if (user.role !== 'Administrador') {
      showToast(t('errors.noAccessToUserManagement'), 'error');
      navigate('/');
      return;
    }

    setIsCheckingSecurity(false);
  };

  useEffect(() => {
    if (user?.id) {
      validateSecurityAccess();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only fetch users if security check passed
    if (!isCheckingSecurity && user?.role === 'Administrador') {
      fetchUsers();
    }
  }, [isCheckingSecurity, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await userService.getAll();
      if (response.success) {
        setUsers(response.data);
      } else {
        setError(response.error.message || 'Failed to fetch users');
      }
    } catch (error: unknown) {
      handleApiError(error, () => {
        setError('Failed to fetch users. Please try again later.');
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (user: User) => {
    // Edición de usuario - sin token check (ruta no importante)
    navigate(`/users/edit/${user.id}`);
  };

  const handleDeactivate = async (id: string) => {
    try {
      const response = await userService.deactivate(id);
      if (response.success) {
        // Refresh the entire table instead of filtering
        await fetchUsers();
      } else {
        setError(response.error.message || 'Failed to deactivate user');
      }
    } catch (error: unknown) {
      handleApiError(error, () => {
        setError('Failed to deactivate user. Please try again later.');
      });
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      const response = await userService.reactivate(id);
      if (response.success) {
        // Refresh the entire table
        await fetchUsers();
      } else {
        setError(response.error.message || 'Failed to reactivate user');
      }
    } catch (error: unknown) {
      handleApiError(error, () => {
        setError('Failed to reactivate user. Please try again later.');
      });
    }
  };

  const handleManageDevices = async (user: User) => {
    // Gestión de dispositivos de usuario - con token check (ruta importante)
    await navigateWithTokenCheck(navigate, `/users/${user.id}/devices`);
  };

  // Show loading while checking security
  if (isCheckingSecurity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-brand-white py-4">
      <LoadingOverlay isVisible={isLoading} message="Cargando usuarios..." />

      <div
        className={`max-w-[100%] mx-auto space-y-6 ${isLoading ? 'blur-sm pointer-events-none' : ''}`}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Users2 className="h-7 w-7 text-brand-gray-dark" />
            <h1 className="text-3xl font-semibold text-brand-gray-dark">
              {t('pages.users.title')}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchUsers}
              disabled={isLoading}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
            <button
              onClick={() => navigate('/users/new')}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
            >
              <UserPlus className="h-5 w-5" />
              {t('pages.users.createNew')}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <strong className="font-bold">{t('common.error')}!</strong>
            <span className="block sm:inline"> {error}</span>
            <button
              className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 font-semibold py-2 px-4 rounded"
              onClick={fetchUsers}
            >
              Retry
            </button>
          </div>
        )}

        <UserTable
          users={users}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          onReactivate={handleReactivate}
          onManageDevices={handleManageDevices}
        />
      </div>
    </div>
  );
};
